import { Text } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { Box, FormControl, Stack } from "@mui/material";
import { NoAgreementTemplateFileAvailable } from "@src/appV2/Agreements/NoAgreementTemplateFileAvailable";
import { type Agreement, type SignedAgreement } from "@src/appV2/Agreements/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type RefObject, useState } from "react";

import { type SignedAtStage } from "../../paths";
import { useSignJobSearchAgreement } from "../api/useSignJobSearchAgreement";
import { useJobSearchAgreementForm } from "../hooks/useJobSearchAgreementForm";
import { JobSearchAgreementBanners } from "./JobSearchAgreementBanners";
import { JobSearchAgreementConfirmationCheckbox } from "./JobSearchAgreementConfirmationCheckbox";

const SUBMIT_BUTTON_TEXT = "I have read and agree to this Job Search User Agreement";

interface JobSearchAgreementFormProps {
  worker: Worker;
  agreementToSign: Agreement | SignedAgreement | undefined;
  agreementsStatus: "loading" | "error" | "success";
  onAcceptAgreement: () => Promise<void>;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  signedAtStage: SignedAtStage;
  placementId?: string;
  isOptional?: boolean;
  onSkip?: () => void;
}

export function JobSearchAgreementForm(props: JobSearchAgreementFormProps) {
  const {
    worker,
    agreementsStatus,
    onAcceptAgreement,
    agreementToSign,
    scrollContainerRef,
    signedAtStage,
    placementId,
    isOptional = false,
    onSkip,
  } = props;

  const { showErrorToast } = useToast();

  const { mutateAsync: signJobSearchAgreement, isLoading: isSigning } = useSignJobSearchAgreement({
    onSuccess: async () => {
      logEvent(APP_V2_USER_EVENTS.JOB_SEARCH_AGREEMENT_SIGNED, {
        workerId: worker.userId,
        name: worker.name,
        agreementVersion: agreementToSign?.attributes.version,
        signedAtStage,
      });
      await onAcceptAgreement();
    },
    onError: (error) => {
      logError(APP_V2_APP_EVENTS.SIGN_JOB_SEARCH_AGREEMENT_FAILURE, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          workerName: worker.name,
          agreementVersion: agreementToSign?.attributes.version,
        },
      });
      showErrorToast("Failed to sign agreement. Please try again.");
    },
  });

  const { formMethods } = useJobSearchAgreementForm({
    workerId: worker.userId,
    agreementVersion: agreementToSign?.attributes.version,
    signedAtStage,
    placementId,
  });

  const {
    formState: { errors, isValid: formIsValid, submitCount },
    control,
    register,
    handleSubmit,
  } = formMethods;

  const [dismissedAtSubmitCount, setDismissedAtSubmitCount] = useState(0);

  const hasErrors = Boolean(errors.agreementScrolled) || Boolean(errors.confirmation);
  const showBanner = submitCount > dismissedAtSubmitCount && hasErrors;

  return (
    <>
      <PageContent ref={scrollContainerRef}>
        {showBanner && (
          <JobSearchAgreementBanners
            worker={worker}
            showAgreementScrolledErrorBanner={Boolean(errors.agreementScrolled)}
            showConfirmationErrorBanner={Boolean(errors.confirmation)}
            onDismiss={() => {
              setDismissedAtSubmitCount(submitCount);
            }}
          />
        )}

        <Box sx={{ flex: "1 1 auto", overflow: "auto", marginTop: 6 }}>
          {agreementsStatus === "loading" && <PdfViewerLoadingSkeleton />}
          {agreementsStatus === "error" && <NoAgreementTemplateFileAvailable />}
          {agreementsStatus === "success" && <PdfViewer />}
        </Box>
        <FormControl component="fieldset" variant="standard">
          <input type="hidden" {...register("agreementScrolled")} />
        </FormControl>
      </PageContent>

      <PageFooterWrapper>
        <Stack spacing={4}>
          <Text variant="body2" color="error">
            You must read the full agreement by scrolling to the bottom. Click the checkbox to
            confirm and continue.
          </Text>

          <JobSearchAgreementConfirmationCheckbox
            worker={worker}
            control={control}
            agreementVersion={agreementToSign?.attributes.version}
            signedAtStage={signedAtStage}
            placementId={placementId}
          />

          <Stack alignItems="center">
            <LoadingButton
              fullWidth
              size="large"
              variant={formIsValid ? "contained" : "outlined"}
              disabled={agreementsStatus === "loading"}
              isLoading={agreementsStatus === "loading" || isSigning}
              sx={{ lineHeight: "1.5rem" }}
              onClick={handleSubmit(async () => {
                if (!isDefined(agreementToSign)) {
                  logError(APP_V2_APP_EVENTS.AGREEMENT_TO_SIGN_NOT_DEFINED, {
                    error: new Error("Job Search Agreement to sign is not defined"),
                    metadata: {
                      workerId: worker.userId,
                      workerName: worker.name,
                    },
                  });
                  showErrorToast(
                    "Unable to sign the agreement. Please try again or contact support."
                  );
                  return;
                }
                await signJobSearchAgreement({
                  agreementVersion: agreementToSign.attributes.version,
                  fullName: worker.name,
                });
              })}
            >
              {SUBMIT_BUTTON_TEXT}
            </LoadingButton>
            {isOptional && onSkip && (
              <TextButton
                underline
                sx={(theme) => ({
                  mt: 2,
                  height: "auto",
                  color: theme.palette.text.tertiary,
                  borderBottomColor: theme.palette.text.tertiary,
                  "& .MuiTypography-root": {
                    color: theme.palette.text.secondary,
                  },
                })}
                onClick={onSkip}
              >
                Skip
              </TextButton>
            )}
          </Stack>
        </Stack>
      </PageFooterWrapper>
    </>
  );
}
